import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { FiMail, FiLock, FiEye, FiEyeOff, FiUser } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import './AuthPages.css';

const REMEMBERED_EMAIL_KEY = 'rememberedEmail';

const CreatorLoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const rememberedEmail = localStorage.getItem(REMEMBERED_EMAIL_KEY) || '';
  const [rememberMe, setRememberMe] = useState(Boolean(rememberedEmail) || true);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues: { email: rememberedEmail }
  });

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const response = await login(data.email, data.password, rememberMe);

      // Check if user is a creator
      if (response.user.role !== 'creator') {
        toast.error('עמוד זה מיועד למשפיענים בלבד');
        return;
      }

      if (rememberMe) {
        localStorage.setItem(REMEMBERED_EMAIL_KEY, data.email);
      } else {
        localStorage.removeItem(REMEMBERED_EMAIL_KEY);
      }

      toast.success('התחברת בהצלחה!');
      navigate('/dashboard');
    } catch (error) {
      // Check if it's an approval error
      if (error.message && error.message.includes('לא אושר')) {
        toast.warning(error.message);
      } else {
        toast.error(error.message || 'אימייל או סיסמה שגויים');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        {/* Left Side - Form */}
        <motion.div
          className="auth-form-section"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <div className="auth-form-content">
            <Link to="/" className="logo">
              <span className="logo-ai">AI</span>
              <span className="logo-weon">weOn</span>
            </Link>

            <div className="login-type-badge">
              <FiUser /> כניסה למשפיענים
            </div>

            <h1>שלום שוב!</h1>
            <p className="auth-subtitle">התחבר לפרופיל שלך כדי לראות קמפיינים חדשים</p>

            <form onSubmit={handleSubmit(onSubmit)} className="auth-form">
              <div className="form-group">
                <label className="form-label">אימייל</label>
                <div className="input-with-icon">
                  <FiMail className="input-icon" />
                  <input
                    type="email"
                    autoComplete="username"
                    className={`form-input ${errors.email ? 'error' : ''}`}
                    placeholder="your@email.com"
                    {...register('email', {
                      required: 'אימייל הוא שדה חובה',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'כתובת אימייל לא תקינה'
                      }
                    })}
                  />
                </div>
                {errors.email && (
                  <span className="form-error">{errors.email.message}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">סיסמה</label>
                <div className="input-with-icon">
                  <FiLock className="input-icon" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    className={`form-input ${errors.password ? 'error' : ''}`}
                    placeholder="הסיסמה שלך"
                    {...register('password', {
                      required: 'סיסמה היא שדה חובה'
                    })}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
                {errors.password && (
                  <span className="form-error">{errors.password.message}</span>
                )}
              </div>

              <div className="form-options">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span>זכור אותי</span>
                </label>
                <Link to="/forgot-password" className="forgot-link">
                  שכחת סיסמה?
                </Link>
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-lg"
                disabled={isLoading}
              >
                {isLoading ? 'מתחבר...' : 'התחבר'}
              </button>
            </form>

            <p className="auth-switch">
              עדיין אין לך חשבון?{' '}
              <Link to="/register">הרשם עכשיו</Link>
            </p>
          </div>
        </motion.div>

        {/* Right Side - Visual */}
        <motion.div
          className="auth-visual-section creator-visual"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <div className="visual-content">
            <h2>קמפיינים חדשים</h2>
            <h2>מותגים מובילים</h2>
            <h2 className="text-primary">ממתינים לך</h2>
            <p>
              היכנס לפרופיל שלך כדי לגלות הזדמנויות
              חדשות לשיתופי פעולה עם מותגים
            </p>
          </div>
          <div className="visual-shapes">
            <div className="shape shape-1"></div>
            <div className="shape shape-2"></div>
            <div className="shape shape-3"></div>
            <div className="shape shape-4"></div>
            <div className="shape shape-5"></div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CreatorLoginPage;
